// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@locus-taxy/neo-ui';

import withRouteTracker from 'common/middleware/RouteTracker';
import Products from 'common/enums/Product';
import ClientConfigService from 'common/services/ClientConfigService';
import { lazy } from 'common/modules/mfe';

import useClientId from 'common/hooks/useClientId';
import usePersonnel from 'common/hooks/usePersonnel';

import RoleOperationEnum from 'common/enums/RoleOperationEnum';
import { ProductSettings, useCanShowProductSetting } from 'platform/settings';

import { usePlatformSettings } from 'common/modules/platform-settings';

const RoleMaster = lazy(() => import('platform-app/roleMaster/RoleMaster'));

const AuditTrailContainer = lazy(() =>
    import('platform-app/auditTrail/AuditTrailContainer')
);

const PremiumFeaturesContainer = lazy(() =>
    import('platform-app/premiumFeatures/PremiumFeaturesContainer')
);

type Routes = Array<{
    id: string,
    path: string,
    title: string,
    Component: React.AbstractComponent<any>,
    disabled?: boolean
}>;

export const useGetRoutes = (): Routes => {
    const { t } = useTranslation();
    const clientId = useClientId();
    const personnel = usePersonnel();
    const settings = usePlatformSettings();

    const canShowProductSetting = useCanShowProductSetting();

    return React.useMemo(() => {
        return [
            {
                id: 'roles',
                path: `/client/${clientId}/settings/roles`,
                title: t('platform:masterRole.title'),
                Component: withRouteTracker(RoleMaster, Products.PLATFORM),
                disabled: !(
                    ClientConfigService.isRbacEnabled() &&
                    personnel.hasPermission(RoleOperationEnum.GET_ROLES)
                )
            },
            {
                id: 'audit-trail',
                path: `/client/${clientId}/settings/audit-trail`,
                title: t('platform:auditTrail.title'),
                Component: withRouteTracker(
                    () => <AuditTrailContainer />,
                    Products.PLATFORM
                ),
                disabled: !(
                    settings.platformSettings.auditTrailSettings?.enabled &&
                    ClientConfigService.isRbacEnabled() &&
                    personnel.hasPermission(RoleOperationEnum.GET_AUDIT_TRAIL)
                )
            },
            ...[
                {
                    product: 'shipflex',
                    disabled: !canShowProductSetting('orchestration')
                },
                {
                    product: 'platform',
                    disabled: !canShowProductSetting('platform')
                },
                {
                    product: 'trackIQ',
                    disabled: !canShowProductSetting('trackIQ')
                },
                {
                    product: 'dispatchIQ',
                    disabled: !canShowProductSetting('dispatchIQ')
                },
                {
                    product: 'orderIQ',
                    disabled: !canShowProductSetting('orderIQ')
                }
            ].map(({ product, disabled = false }) => ({
                id: `${product}`,
                path: `/client/${clientId}/settings/${product}`,
                title: t(`platform:settings.products.${product}`),
                Component: withRouteTracker(
                    () => <ProductSettings product={product} />,
                    Products.PLATFORM
                ),
                disabled
            })),
            {
                id: 'premium',
                path: `/client/${clientId}/settings/premium`,
                title: t('platform:premiumFeatures.title'),
                icon: (
                    <Icon
                        icon="settings_premium_feature"
                        color="yellow.500"
                        size="medium"
                    />
                ),
                Component: withRouteTracker(
                    PremiumFeaturesContainer,
                    Products.PLATFORM
                ),
                disabled: !(
                    ClientConfigService.isUpsellEnabled() &&
                    settings.upsellSettings.isEnabled &&
                    (personnel.isGlobalPersonnel() ||
                        (ClientConfigService.isRbacEnabled() &&
                            (personnel.hasPermission(
                                RoleOperationEnum.UPDATE_PLATFORM_SETTINGS
                            ) ||
                                personnel.hasPermission(
                                    RoleOperationEnum.UPDATE_TRACK_IQ_SETTINGS
                                ) ||
                                personnel.hasPermission(
                                    RoleOperationEnum.UPDATE_DISPATCHER_SETTINGS
                                ))))
                )
            }
        ];
    }, [clientId, t, personnel, settings, canShowProductSetting]);
};
